import "../styles/AdminInterface.css"
import React, { useState, useEffect, useMemo } from 'react'
import BackendAPI from "../API/BackendAPI"
import IconButton from '@mui/material/IconButton';

import { useHistory } from 'react-router-dom';
import { FormControl, InputLabel, Tooltip, Divider, Typography, TableContainer, TableHead, TableRow, Paper, TableBody, Table, TableCell, Select, MenuItem } from "@material-ui/core"
import DownloadIcon from '@mui/icons-material/Download';
import TimerIcon from '@mui/icons-material/Timer';

import Sidebar from "../components/Sidebar"
import cookie from 'react-cookies'
import { formatDuree, formatDate, telechargerCSV, aujourdhui } from "../utils/csv"


// ─────────────────────────────────────────────────────────────
// Devis 2026-1 : meilleurs scores et temps de jeu PAR NIVEAU.
// Interface alignée sur la page « Scores » de l'administration
// Play & Learn : mêmes filtres (sauf Programme, absent d'Accounting),
// même survol, mêmes exports CSV.
// ─────────────────────────────────────────────────────────────
const NUMEROS_JEUX = {
  "1 - Cupcakes Battle": 11,
  "2 - Find the Balance": 12,
  "3 - What a bun!": 13,
  "4 - Crazy Coo-Quiz Accounting": 14,
  "5 - Crazy Coo-Quiz Financial Analysis": 15,
}
const NOMS_JEUX = Object.fromEntries(Object.entries(NUMEROS_JEUX).map(([nom, num]) => [num, nom]))
const LISTE_JEUX = ["Tous", ...Object.keys(NUMEROS_JEUX)]

/** "2 - Find the Balance" → 12 ; "Tous" → null */
const jeuVersNum = (libelle) => NUMEROS_JEUX[libelle] ?? null

/** Année scolaire : du 25 août au 24 août (mêmes bornes que Play & Learn). */
const debutAnneeScolaire = (d = new Date()) =>
  (d.getMonth() > 7 || (d.getMonth() === 7 && d.getDate() >= 25)) ? d.getFullYear() : d.getFullYear() - 1

/** Premières parties Accounting : février 2025 (année 2024-2025). */
const PREMIERE_ANNEE = 2024

const optionsAnnees = () => {
  const options = ["Tous"]
  for (let a = PREMIERE_ANNEE; a <= debutAnneeScolaire(); a++) options.push(`${a}-${a + 1}`)
  return options
}

/** "2025-02-12T18:31:19." / "2026-10-04 21:05:00" / "2026-10-04" → Date (jour), sinon null */
const jourDe = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null
  const p = dateStr.slice(0, 10).split("-")
  if (p.length < 3) return null
  const d = new Date(+p[0], (+p[1]) - 1, +p[2])
  return isNaN(d) ? null : d
}

/** La date tombe-t-elle dans l'année scolaire « 2026-2027 » (25 août → 24 août) ? */
const dansAnnee = (dateStr, annee) => {
  if (!annee || annee === "Tous") return true
  const d = jourDe(dateStr)
  if (!d) return false
  const a = parseInt(annee.split("-")[0], 10)
  return d >= new Date(a, 7, 25) && d <= new Date(a + 1, 7, 24)
}

const estRenseigne = (v) => v !== null && v !== undefined && String(v).trim() !== ""
// Comparaison en texte : une même valeur peut arriver en nombre ou en texte
// selon l'échange (groupe « 3 », pseudo « 16 »).
const memeValeur = (a, b) => String(a ?? "").trim() === String(b ?? "").trim()

const valeursUniques = (lignes, attribut) => {
  const valeurs = [...new Set(lignes.map((l) => l[attribut]).filter(estRenseigne).map((v) => String(v).trim()))]
    .sort((a, b) => String(a).localeCompare(String(b)))
  return ["Tous", ...valeurs]
}

const AdminClassements = () => {
  const history = useHistory()

  const user = cookie.load("user")
  const handleDisconnect = () => {
    cookie.save('user', "disconnected", {
      path: '/',
      maxAge: 60 * 60 * 1.5, //sec
    })
    history.push('/login');
  }

  if (user === null || user === undefined || user === "disconnected") history.push("/login");
  if (user !== null && user !== undefined && user.username !== undefined && !user.username.toUpperCase().includes("ADMIN")) handleDisconnect()


  const [userList, setUserList] = useState([])
  const [erreurClassement, setErreurClassement] = useState(false)

  const anneesOptions = useMemo(() => optionsAnnees(), [])
  // « Tous » par défaut : c'est ce qu'affichait cette page jusqu'ici (pas de
  // filtre d'année). Choisir une année filtre comme dans Play & Learn.
  const [annee, setAnnee] = useState("Tous")
  const [jeu, setJeu] = useState("Tous")
  const [nom, setNom] = useState('Tous');
  const [prenom, setPrenom] = useState('Tous');
  const [pseudo, setPseudo] = useState('Tous');
  const [campus, setCampus] = useState('Tous');
  const [programme, setProgramme] = useState('Tous');
  const [groupe, setGroupe] = useState('Tous');

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
  };

  useEffect(() => {
    const getClassement = async () => {
      try {
        const response = await BackendAPI.getClassements()
        if (response && response.connectionApprouvee && Array.isArray(response.classement)) {
          setUserList(response.classement);
        } else {
          console.error("Connection not approved:", response);
          setErreurClassement(true)
        }
      } catch (error) {
        console.error("Error fetching classement:", error);
        setErreurClassement(true)
      }
    };
    getClassement();
  }, []);


  // Détail par niveau, rechargé à chaque changement d'année scolaire
  // (filtrage fait côté serveur sur la date réelle de chaque partie).
  const [scoresNiveaux, setScoresNiveaux] = useState([])
  // 'chargement' | 'ok' | 'erreur' : sans cet état, un serveur injoignable
  // donnait « pas encore de détail par niveau » partout.
  const [etatNiveaux, setEtatNiveaux] = useState('chargement')

  useEffect(() => {
    // Une réponse arrivée après un changement d'année est ignorée : sinon le
    // survol et l'export pouvaient afficher les données d'une autre année.
    let actif = true
    const charger = async () => {
      let dateDebut = null
      let dateFin = null
      if (annee && annee !== "Tous") {
        const a = parseInt(annee.split("-")[0], 10)
        dateDebut = `${a}-08-25`       // mêmes bornes que dansAnnee
        dateFin = `${a + 1}-08-24`
      }
      const donnees = await BackendAPI.getScoresNiveaux(dateDebut, dateFin)
      if (!actif) return
      setScoresNiveaux(donnees || [])
      setEtatNiveaux(donnees === null ? 'erreur' : 'ok')
    }
    setScoresNiveaux([])
    setEtatNiveaux('chargement')
    charger()
    return () => { actif = false }
  }, [annee])

  /** idUser → lignes (jeu, niveau) */
  const niveauxParEtudiant = useMemo(() => {
    const index = {}
    scoresNiveaux.forEach((l) => {
      if (!index[l.idUser]) index[l.idUser] = []
      index[l.idUser].push(l)
    })
    Object.values(index).forEach((lignes) => lignes.sort((a, b) => a.numJeu - b.numJeu || a.niveau - b.niveau))
    return index
  }, [scoresNiveaux])


  // Lignes (étudiant × jeu) retenues par l'année et le jeu
  const lignesPeriode = useMemo(() => {
    const num = jeuVersNum(jeu)
    return userList
      .filter((l) => num === null || Number(l.numJeu) === num)
      .filter((l) => dansAnnee(l.dateUpdate, annee))
  }, [userList, jeu, annee])

  // Options des filtres : chaque liste ne propose que des valeurs présentes
  // avec les autres filtres déjà choisis.
  // Campus → Programme → Groupe, comme Play & Learn.
  const campusOptions = useMemo(() => valeursUniques(lignesPeriode, "campus"), [lignesPeriode])
  const lignesCampus = useMemo(
    () => lignesPeriode.filter((l) => campus === "Tous" || memeValeur(l.campus, campus)),
    [lignesPeriode, campus])
  const programmeOptions = useMemo(() => valeursUniques(lignesCampus, "programme"), [lignesCampus])
  const groupesOptions = useMemo(
    () => valeursUniques(lignesCampus.filter((l) => programme === "Tous" || memeValeur(l.programme, programme)), "groupe"),
    [lignesCampus, programme])
  const lignesCampusGroupe = useMemo(
    () => lignesCampus
      .filter((l) => programme === "Tous" || memeValeur(l.programme, programme))
      .filter((l) => groupe === "Tous" || memeValeur(l.groupe, groupe)),
    [lignesCampus, programme, groupe])
  const nomOptions = useMemo(() => valeursUniques(lignesCampusGroupe, "nom"), [lignesCampusGroupe])
  const prenomOptions = useMemo(
    () => valeursUniques(lignesCampusGroupe.filter((l) => nom === "Tous" || memeValeur(l.nom, nom)), "prenom"),
    [lignesCampusGroupe, nom])
  const pseudosOptions = useMemo(() => valeursUniques(lignesCampusGroupe, "pseudo"), [lignesCampusGroupe])

  // Un filtre dont la valeur n'est plus proposée revient à « Tous ».
  useEffect(() => { if (!campusOptions.includes(campus)) setCampus("Tous") }, [campusOptions, campus])
  useEffect(() => { if (!programmeOptions.includes(programme)) setProgramme("Tous") }, [programmeOptions, programme])
  useEffect(() => { if (!groupesOptions.includes(groupe)) setGroupe("Tous") }, [groupesOptions, groupe])
  useEffect(() => { if (!nomOptions.includes(nom)) setNom("Tous") }, [nomOptions, nom])
  useEffect(() => { if (!prenomOptions.includes(prenom)) setPrenom("Tous") }, [prenomOptions, prenom])
  useEffect(() => { if (!pseudosOptions.includes(pseudo)) setPseudo("Tous") }, [pseudosOptions, pseudo])

  // Classement : un étudiant par ligne, score = somme de ses meilleurs scores
  // sur les jeux retenus, trié du meilleur au moins bon.
  const localUserList = useMemo(() => {
    const parEtudiant = lignesCampusGroupe
      .filter((l) => nom === "Tous" || memeValeur(l.nom, nom))
      .filter((l) => prenom === "Tous" || memeValeur(l.prenom, prenom))
      .filter((l) => pseudo === "Tous" || memeValeur(l.pseudo, pseudo))
      .reduce((acc, l) => {
        const score = Number(l.bestScore) || 0
        if (!acc[l.idUser]) {
          acc[l.idUser] = { ...l, totalBestScore: score, derniereMaj: l.dateUpdate }
        } else {
          acc[l.idUser].totalBestScore += score
          if (String(l.dateUpdate || "") > String(acc[l.idUser].derniereMaj || "")) acc[l.idUser].derniereMaj = l.dateUpdate
        }
        return acc
      }, {})
    return Object.values(parEtudiant).sort((a, b) => b.totalBestScore - a.totalBestScore)
  }, [lignesCampusGroupe, nom, prenom, pseudo])


  const [hoveredRow, setHoveredRow] = useState(null);
  const libelleAnnee = annee === "Tous" ? "Toutes années" : annee

  // Au survol : meilleurs scores de CHAQUE NIVEAU du jeu sélectionné (ou de
  // tous les jeux si « Tous »), avec le nombre de parties et le temps de jeu.
  // Un jeu sans détail par niveau (parties jouées avant la version 3.2.0 de
  // l'application) affiche son meilleur score tous niveaux confondus.
  const renderDetailNiveaux = (u) => {
    if (etatNiveaux === 'chargement') return <i>Chargement du détail par niveau…</i>
    if (etatNiveaux === 'erreur') return <i>Détail par niveau indisponible (serveur injoignable).</i>
    const num = jeuVersNum(jeu)
    const lignes = (niveauxParEtudiant[u.idUser] || []).filter((l) => num === null || l.numJeu === num)
    const parJeu = {}
    lignes.forEach((l) => { (parJeu[l.numJeu] = parJeu[l.numJeu] || []).push(l) })

    const meilleursJeux = {}
    userList
      .filter((l) => l.idUser === u.idUser && Number(l.bestScore) > 0 && dansAnnee(l.dateUpdate, annee))
      .filter((l) => num === null || Number(l.numJeu) === num)
      .forEach((l) => { meilleursJeux[Number(l.numJeu)] = Math.max(meilleursJeux[Number(l.numJeu)] || 0, Number(l.bestScore)) })

    const jeux = [...new Set([...Object.keys(parJeu), ...Object.keys(meilleursJeux)].map(Number))].sort((a, b) => a - b)
    if (jeux.length === 0) return <i>Aucun score sur cette période.</i>

    return (
      <span style={{ fontSize: 13, lineHeight: 1.5 }}>
        {jeux.map((n) => (
          <span key={n}>
            <b>{NOMS_JEUX[n] || `Jeu ${n - 10}`}</b><br />
            {parJeu[n]
              ? parJeu[n].map((l) => (
                <span key={l.niveau}>
                  Niveau {l.niveau} : <b>{l.bestScore} pts</b>
                  {" "}({l.nbParties} partie{l.nbParties > 1 ? "s" : ""} · {formatDuree(l.tempsTotal)})
                  <br />
                </span>
              ))
              : <span>Score du jeu : <b>{meilleursJeux[n]} pts</b> <i>{annee === "Tous" ? "(pas encore de détail par niveau)" : "(aucune partie par niveau sur cette année)"}</i><br /></span>}
            {/* Score du jeu au classement = somme des records de ses niveaux,
                ou l'ancien record du jeu s'il est plus élevé. */}
            {parJeu[n] && parJeu[n].length > 1
              ? <span>Total des niveaux : <b>{parJeu[n].reduce((t, l) => t + (Number(l.bestScore) || 0), 0)} pts</b><br /></span>
              : null}
            {/* Avec une année choisie, le détail ne couvre que les parties de
                l'année, alors que le score du classement est le record toutes
                années confondues : on ne parle donc d'« ancien record » que
                sans filtre d'année. */}
            {parJeu[n] && meilleursJeux[n] > parJeu[n].reduce((t, l) => t + (Number(l.bestScore) || 0), 0)
              ? <span><i>Retenu au classement : {meilleursJeux[n]} pts ({annee === "Tous" ? "record obtenu avant le suivi par niveau" : "records toutes années confondues"})</i><br /></span>
              : null}
          </span>
        ))}
      </span>
    )
  };

  const renderClassements = () => {
    if (userList === null || userList.length === 0) return null
    return (
      <TableContainer component={Paper} style={{ maxHeight: "100%" }} >
        <Table stickyHeader aria-label="customized table">
          <TableHead>
            <TableRow key={"HEAD"}>
              <TableCell align="center" style={{ width: 80 }}>Rang</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Nom</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Prénom</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Pseudo</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Campus</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Programme</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Groupe</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {localUserList.map((u, index) => (
              <TableRow key={u.idUser} style={{ height: 40 }}>
                <TableCell align="center">{index + 1}</TableCell>
                <TableCell align="center">{u.nom}</TableCell>
                <TableCell align="center">{u.prenom}</TableCell>
                <TableCell align="center">{u.pseudo}</TableCell>
                <TableCell align="center">{u.campus}</TableCell>
                <TableCell align="center">{u.programme}</TableCell>
                <TableCell align="center">{u.groupe}</TableCell>
                <TableCell align="center" style={{ cursor: "pointer" }}>
                  <Tooltip
                    title={hoveredRow === u.idUser ? renderDetailNiveaux(u) : ''}
                    placement="right"
                    arrow
                  >
                    <span
                      onMouseEnter={() => setHoveredRow(u.idUser)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      {u.totalBestScore}
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };


  // Export du classement affiché (mêmes colonnes que Play & Learn).
  const exportToCSV = () => {
    if (!localUserList || localUserList.length === 0) {
      alert("Aucun utilisateur à exporter !");
      return;
    }
    const headers = ["Date", "Rang", "Nom", "Prénom", "Pseudo", "Email", "Campus", "Programme", "Groupe", "Score"];
    const rows = localUserList.map((u, index) => [
      u.derniereMaj ? String(u.derniereMaj).slice(0, 10) : "",
      index + 1,
      u.nom,
      u.prenom,
      u.pseudo,
      u.email,
      u.campus,
      u.programme,
      u.groupe,
      u.totalBestScore
    ]);
    telechargerCSV(
      (jeu === "Tous" ? "Classement général" : "Classement " + jeu) + " - " + libelleAnnee + " - " + aujourdhui() + ".csv",
      headers,
      rows
    )
  };


  // Export détaillé : une ligne par étudiant / jeu / niveau, avec meilleur
  // score et temps de jeu. Mêmes filtres que le tableau.
  const exportDetailNiveauxCSV = () => {
    if (etatNiveaux !== 'ok') {
      alert(etatNiveaux === 'chargement'
        ? "Le détail par niveau est en cours de chargement. Réessayez dans quelques secondes."
        : "Le détail par niveau n'a pas pu être chargé (serveur injoignable).")
      return
    }
    const num = jeuVersNum(jeu)
    const lignes = scoresNiveaux
      .filter((l) => num === null || l.numJeu === num)
      .filter((l) => nom === "Tous" || memeValeur(l.nom, nom))
      .filter((l) => prenom === "Tous" || memeValeur(l.prenom, prenom))
      .filter((l) => pseudo === "Tous" || memeValeur(l.pseudo, pseudo))
      .filter((l) => campus === "Tous" || memeValeur(l.campus, campus))
      .filter((l) => programme === "Tous" || memeValeur(l.programme, programme))
      .filter((l) => groupe === "Tous" || memeValeur(l.groupe, groupe))
      .sort((a, b) =>
        String(a.nom).localeCompare(String(b.nom)) ||
        String(a.prenom).localeCompare(String(b.prenom)) ||
        a.numJeu - b.numJeu || a.niveau - b.niveau)

    if (lignes.length === 0) {
      alert("Aucune partie enregistrée par niveau pour ces filtres.")
      return
    }

    const entetes = [
      "Année scolaire", "Nom", "Prénom", "Pseudo", "Email", "Campus", "Programme", "Groupe",
      "Jeu", "Niveau", "Meilleur score",
      "Durée de la partie record", "Durée de la partie record (s)", "Date du meilleur score",
      "Nombre de parties",
      "Temps de jeu total", "Temps de jeu total (s)",
      "Temps moyen par partie", "Temps moyen par partie (s)",
      "Première partie", "Dernière partie",
    ]

    const rows = lignes.map((l) => [
      libelleAnnee, l.nom, l.prenom, l.pseudo, l.email, l.campus, l.programme, l.groupe,
      NOMS_JEUX[l.numJeu] || l.numJeu, l.niveau, l.bestScore,
      formatDuree(l.dureeMeilleurePartie), l.dureeMeilleurePartie, formatDate(l.dateMeilleurScore),
      l.nbParties,
      formatDuree(l.tempsTotal), l.tempsTotal,
      formatDuree(l.tempsMoyen), l.tempsMoyen,
      formatDate(l.premierePartie), formatDate(l.dernierePartie),
    ])

    telechargerCSV(
      (num === null ? "Scores et temps par niveau - tous les jeux" : "Scores et temps par niveau - " + jeu) + " - " + libelleAnnee + " - " + aujourdhui() + ".csv",
      entetes,
      rows
    )
  };

  const renderSelect = (label, value, setter, options) => (
    <FormControl size="small" variant="outlined">
      <InputLabel>{label}</InputLabel>
      <Select value={value} onChange={handleFilterChange(setter)} label={label}>
        {options.map((option) => (
          <MenuItem key={option} value={option} style={{ paddingTop: 8, paddingBottom: 8 }}>{option}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div
        className="body-admin"
      >
        <Typography variant="h1" color="primary">Classements</Typography>
        <Divider />
        <div style={{ margin: 10 }}  >
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            {renderSelect("Année", annee, setAnnee, anneesOptions)}
            {renderSelect("Jeu", jeu, setJeu, LISTE_JEUX)}
            {renderSelect("Nom", nom, setNom, nomOptions)}
            {renderSelect("Prénom", prenom, setPrenom, prenomOptions)}
            {renderSelect("Pseudo", pseudo, setPseudo, pseudosOptions)}
            {renderSelect("Campus", campus, setCampus, campusOptions)}
            {renderSelect("Programme", programme, setProgramme, programmeOptions)}
            {renderSelect("Groupe", groupe, setGroupe, groupesOptions)}
            <Tooltip title="Exporter le classement (CSV)" arrow>
              <IconButton aria-label="download" style={{ padding: 10 }} onClick={exportToCSV}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Exporter les meilleurs scores et temps de jeu par niveau (CSV)" arrow>
              <IconButton aria-label="export par niveau" style={{ padding: 10 }} onClick={exportDetailNiveauxCSV}>
                <TimerIcon color="primary" />
              </IconButton>
            </Tooltip>
          </div>
          <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: -10 }}>
            Score : pour chaque jeu, somme des records de chaque niveau (ou ancien record du jeu s'il est plus élevé), comme Play & Learn.
            Année : le classement retient la date du record le plus récent de chaque jeu ; le survol et l'export par niveau
            (icône chrono) retiennent la date réelle de chaque partie, enregistrée depuis la version 3.2.0 de l'application.
          </Typography>
          <br></br>
          {erreurClassement ?
            <Typography color="error">Le classement n'a pas pu être chargé (serveur injoignable). Rechargez la page.</Typography>
            : null}
          {renderClassements()}
        </div>
      </div></div>
  );
}

export default AdminClassements;
